/**
 * Чтение ответа `/analytics/employees/efficiency`.
 *
 * Эндпоинт отдаёт объект `{stages, rows}`, а не массив строк: состав колонок таблицы
 * операторов задаётся воронкой активного пакета узлов и меняется вместе с ним. У ответа два
 * потребителя — таблица на вкладке «Сотрудники» и распределение звонков по операторам в
 * «Общей статистике», — и оба раньше читали массив напрямую. Разбор вынесен сюда, потому что
 * несовпадение формы не даёт ошибки: график просто молча пустеет.
 */

/** Строки ответа; всё, что не похоже на список строк, читается как пустая выборка. */
export const readEfficiencyRows = (response) => (Array.isArray(response?.rows) ? response.rows : []);

/**
 * Этапы воронки в объявленном порядке пакета. Пустой список — это не ошибка: у тенанта
 * может не быть активного пакета или ноды воронки, и тогда таблица показывает только
 * «Сотрудник», «Конверсия» и «Чек-лист».
 */
export const readFunnelStages = (response) => (Array.isArray(response?.stages) ? response.stages : []);

/** Накопительное число звонков, дошедших до этапа с указанным индексом. */
export const readStageCount = (row, stageIndex) => Number(row?.stageCounts?.[stageIndex]) || 0;

/**
 * Всего звонков оператора за период. Считается по таблице `calls` и от пайплайна нод не
 * зависит: звонок, которого воронка не коснулась, всё равно попадает в это число. Поэтому
 * колонка отвечает на вопрос, на который этапы воронки ответить не могут, — были ли у
 * оператора звонки вообще.
 */
export const readCallCount = (row) => Number(row?.calls) || 0;

/**
 * Звонки оператора с жалобой за период — тем же правилом, что раздел «Жалобы». Считаются
 * звонки, а не кейсы: кейс (клиент за день) бывает общим у нескольких операторов, поэтому
 * сумма столбца не обязана совпадать с итогом раздела «Жалобы».
 */
export const readComplaintCount = (row) => Number(row?.complaints) || 0;

/**
 * Оформление ячейки «Жалобы»: ноль приглушён, любая жалоба — красная плашка, чтобы оператор
 * с жалобами находился взглядом, без сортировки.
 */
export const resolveComplaintStyle = (count) =>
  count > 0
    ? { label: count.toLocaleString('ru-RU'), tone: 'bad' }
    : { label: '0', tone: 'muted' };

/**
 * Число из ответа или `null`. Пустая строка и `null` — это «значения нет», а не ноль:
 * `Number('')` дал бы 0, и строка без оценки встала бы в рейтинг худшей.
 */
export const readFiniteNumber = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

/**
 * Хватает ли звонков, чтобы конверсия строки участвовала в сравнении лучших и худших. Порог
 * считает сервер (`lowSample` — меньше N применимых звонков). Нет флага — «не хватает»:
 * подсветить строку по неполному ответу хуже, чем не подсветить.
 */
export const hasEnoughConversionData = (row) => row?.lowSample === false;

/** То же для чек-листа: порог тот же, но по звонкам с оценкой (`checklistLowSample`). */
export const hasEnoughChecklistData = (row) => row?.checklistLowSample === false;

/**
 * Порог достоверности из ответа — для подсказок у отметки «Недостаточно данных». Своей копии
 * числа у фронтенда нет: нет порога в ответе — подсказка пишется без числа.
 */
export const readLowSampleThreshold = (response) => {
  const threshold = Number(response?.lowSampleThreshold);
  return Number.isInteger(threshold) && threshold > 0 ? threshold : null;
};

/** Липкая первая колонка: на узком экране имя сотрудника остаётся видимым при прокрутке вбок. */
export const EMPLOYEE_COLUMN_CLASS = 'max-sm:sticky max-sm:left-0 max-sm:z-10 max-sm:bg-card';

/**
 * Колонки таблицы «Эффективность операторов» в порядке отрисовки.
 *
 * Единственный источник состава: по нему строятся и заголовки, и `colSpan` пустого состояния.
 * Пока это были два независимых места, число колонок и `colSpan` разъезжались при каждой правке.
 *
 * «Звонки» стоят в фиксированной части, до этапов: величина считается по таблице `calls` и от
 * пакета узлов не зависит — у оператора, которого воронка не коснулась ни разу, звонки всё равно
 * были. Ради этого случая колонка и нужна: без неё строка из одних нулей неотличима от строки
 * оператора, у которого звонков не было вовсе.
 *
 * «Жалобы» — последними, рядом с остальными сигналами качества. Подсказка заголовка объясняет
 * единицу счёта: без неё сумма столбца выглядела бы расхождением с разделом «Жалобы».
 *
 * Сортируется каждый столбец. `firstSortDirection` — направление первого клика: имя — по
 * алфавиту, у остальных первым кликом сверху оказывается большее (`employeeEfficiencySort.js`).
 */
export const buildEfficiencyColumns = (stages) => [
  { key: 'employee', label: 'Сотрудник', className: EMPLOYEE_COLUMN_CLASS, firstSortDirection: 'asc' },
  { key: 'calls', label: 'Звонки', numeric: true },
  ...readFunnelStages({ stages }).map((stage) => ({
    key: `stage:${stage.code}`,
    label: stage.label || stage.code,
    numeric: true
  })),
  { key: 'conversion', label: 'Конверсия' },
  { key: 'checklist', label: 'Чек-лист' },
  {
    key: 'complaints',
    label: 'Жалобы',
    numeric: true,
    title: 'Звонки сотрудника, в которых анализ выявил жалобу клиента'
  }
];
